import { useCallback, useEffect, useId, useMemo, useRef } from "react";
import { useFieldSetContext } from "../contexts/field-set-context";
import { MAIN_SUBMIT_BUTTON_NAME, SubmitButtonApi } from "../shared/types";
import { focusElement } from "../shared/utils";

export interface useSubmitButtonProps {
    name?: string,
    focus?: (select?: boolean) => boolean
}

export default function useSubmitButton({
    name = MAIN_SUBMIT_BUTTON_NAME,
    focus
}: useSubmitButtonProps = {}): SubmitButtonApi {
    const fieldSet = useFieldSetContext();
    const ref = useRef<HTMLElement>(null!);

    const id = name + "-" + useId();
    const isSubmitting = id === fieldSet.submitterId

    const handleFocus = useCallback((select?: boolean) => {
        if (focus) return focus(select);
        return focusElement(ref.current, select);
    }, [focus]);

    const handleSubmit = useCallback(() => {
        return fieldSet.submit(id);
    }, [])

    useEffect(() => {
        fieldSet.register({
            id,
            name,
            element: ref.current,
            type: "submitButton",
            focus: handleFocus,
        });
        return () => fieldSet.unregister(id);
    }, [id, name, handleFocus]);

    return useMemo(() => ({
        id,
        name,
        ref,
        fieldSet,
        isSubmitting,
        submit: handleSubmit
    }), [id, name, fieldSet])
}